import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WorkbenchConfiguration } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration';
import {
  WB_COLORS,
  WB_FINISHES,
  WB_PLAN_GROUPS,
  WB_SPLASHBACK,
  WB_SUMMARY,
} from 'src/app/providers/workbench.provider';
import { WorkbenchService } from 'src/app/services/configurators-2d/applications/workbench.service';
import { BaseConfigurator } from '../configurator/base-configurator';
import { ConfiguratorStructure } from '../configurator/configurator-structure';
import { ConfigurationGroup } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-group';
import { isPlanDimensionsValid } from 'src/app/models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { ConfiguratorUpdateStructureStep } from '../configurator/configurator-update-structure-step';
import { MaterialTypeEnum } from 'src/app/enum/workbench/material-type-enum';
import { TunnelNavigationStore } from '../../../stores/tunnel/tunnel-navigation.store';
import { TunnelDataStore } from '../../../stores/tunnel/tunnel-data.store';
import { Subscription } from 'rxjs';
import { CONFIG_CONTEXT } from '../../../enum/config-context.enum';
import { KitchenConfigurationData } from '../../../stores/tunnel/model/configuration-data/kitchen-configuration-data';

@Component({
  selector: 'ng-workbench-configuration',
  templateUrl: './workbench.component.html',
})
export class WorkbenchComponent
  extends BaseConfigurator<WorkbenchConfiguration, WorkbenchService>
  implements OnInit, OnDestroy {
  // Etape de la configuration
  @Input() step: number = 1;

  @Output() setGlobalLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() getFinishesStep: EventEmitter<any> = new EventEmitter<boolean>();

  // Update du fil d'arianne
  @Output() updateStepperItems: EventEmitter<ConfiguratorUpdateStructureStep> =
    new EventEmitter<ConfiguratorUpdateStructureStep>();

  /** Changement de page */
  @Output() changePage: EventEmitter<any> = new EventEmitter<boolean>();

  /** Crédence seule */
  @Output() splashbackChanged = new EventEmitter<boolean>();

  @Output() disableNextButton = new EventEmitter<boolean>();

  // Resume only splashback
  isOnlySplashback = false;

  // Url de base des images
  baseImgUrl: string = undefined;

  // Loading local
  isLoading: boolean = false;

  // Splashbacks
  splashback: boolean = false;

  hasFinishesStep: boolean = true;

  private readonly subscriptions: Subscription[] = [];
  private existingWorkbenchConfigurationId: string;
  private existingMainContextConfigurationId: string;
  private mainContext: CONFIG_CONTEXT;

  get isTunnel() {
    return this.tunnelNavigationStore?.isTunnelValue;
  }

  constructor(
    private readonly route: ActivatedRoute,
    workbenchService: WorkbenchService,
    protected injector: Injector,
    private readonly tunnelNavigationStore: TunnelNavigationStore,
    private readonly tunnelDataStore: TunnelDataStore
  ) {
    super(workbenchService, injector);
    this.baseImgUrl = this.service.apiUrl;
  }

  ngOnInit(): void {
    this.isLoading = true;

    if (this.isTunnel) {
      this.subscribeToTunnelObservable();
    }

    setTimeout(async () => {
      if (this.existingWorkbenchConfigurationId) {
        await this.loadConfigurationFromId(this.existingWorkbenchConfigurationId);
      } else {
        await this.loadConfigurationFromRoute();
      }

      this.setGlobalLoading.emit(false);
      this.isLoading = false;
    }, 0);
  }

  ngOnDestroy() {
    if (!this.configuration.inTunnel) {
      this.service.reset();
    }

    this.subscriptions?.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  private async loadConfigurationFromId(workbenchConfigurationId: string) {
    await this.service.getProjectById(workbenchConfigurationId, true);
    this.loadConfiguration();
  }

  private async loadConfigurationFromRoute() {
    await this.loadExistingConfigurationIfNeeded(this.route);
    this.loadConfiguration();
  }

  private subscribeToTunnelObservable() {
    this.subscriptions.push(
      this.tunnelDataStore.getMainContext().subscribe(mainContext => this.mainContext = mainContext),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData && this.mainContext === CONFIG_CONTEXT.KITCHEN) {
          const kitchenConfigurationData = configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;
          this.existingWorkbenchConfigurationId = kitchenConfigurationData.workbenchConfigurationId;
          this.existingMainContextConfigurationId = kitchenConfigurationData.HBMConfigurationId;
        }
      })
    );
  }

  private loadConfiguration() {
    if (!this.configuration) return;
    // Si on a des plans et s'ils font partis d'un groupe, on créer les groupes de référence.
    this.configuration.workbenchConfigurationGroups ??= [];

    if (this.configuration.workbenchConfigurationPlans?.length > 0) {
      this.configuration.workbenchConfigurationPlans?.forEach(plan => {
        if (plan.isInGroup) {
          const group = this.configuration.workbenchConfigurationGroups?.find(
            group => group.id === plan.groupReferenceId
          );
          if (group) {
            group.workbenchConfigurationPlans.push(plan);
          } else {
            let newGroup = new ConfigurationGroup(
              this.configuration.workbenchConfigurationGroups?.length + 1,
              plan.groupReferenceId
            );
            newGroup.workbenchConfigurationPlans.push(plan);
            this.configuration.workbenchConfigurationGroups.push(newGroup);
          }
        }
      });
    }

    if (
      this.configuration.workbenchConfigurationPlans?.some(p => p.material?.materialType === MaterialTypeEnum.Quartz)
    ) {
      this.isSplashbackNeeded(true);
      if (this.configuration.workbenchConfigurationPlans[0]?.configurationSplashbacks[0]?.isOnlySplashback) {
        this.isFinishesSplashback(false);
      } else {
        this.isFinishesSplashback(true);
      }
    }
    this.configuration.inTunnel ??= this.isTunnel;
    if (this.isTunnel) {
      this.configuration.tunnelContext ??= this.mainContext;
      this.configuration.tunnelId ??= this.existingMainContextConfigurationId;
    }

    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.WORKBENCH, this.configuration);
  }

  override initConfigurator(): ConfiguratorStructure {
    return new ConfiguratorStructure([
      {
        index: 1,
        title: 'workbench.steps.plan-shapes',
        injectionToken: WB_PLAN_GROUPS,
        gtmTitle: 'Plan de travail | Formes des plans',
        gtmPageName: 'plans',
      },
      {
        index: 2,
        title: 'workbench.steps.colors',
        injectionToken: WB_COLORS,
        gtmTitle: 'Plan de travail | Coloris',
        gtmPageName: 'coloris',
      },
      {
        index: 3,
        title: 'workbench.steps.finishes',
        injectionToken: WB_FINISHES,
        gtmTitle: 'Plan de travail | Finitions',
        gtmPageName: 'finitions',
      },
      {
        index: 4,
        title: 'workbench.steps.summary',
        injectionToken: WB_SUMMARY,
        gtmTitle: 'Plan de travail | Prix et Résumé',
        gtmPageName: 'prix-et-resume',
      },
    ]);
  }

  /** Fonction de désactivation du bouton Enregistrer */
  override isSaveProjectButtonDisabled(): boolean {
    if (this.configuration) {
      return !(
        this.configuration.workbenchConfigurationPlans?.length > 0 &&
        this.configuration.workbenchConfigurationPlans?.every(plan => isPlanDimensionsValid(plan))
      );
    }

    return true;
  }

  isSplashbackNeeded(isSplashback: boolean) {
    this.splashback = isSplashback;
    const step = {
      index: 3,
      title: 'workbench.steps.splashback',
      injectionToken: WB_SPLASHBACK,
      gtmTitle: 'Plan de travail | Crédences',
      gtmPageName: 'credences',
    };

    if (!isSplashback) {
      this.isFinishesSplashback(true);
    }

    this.updateStepperItems.emit({ isInsert: isSplashback, step });
  }

  isFinishesSplashback(isSplashback: boolean) {
    this.hasFinishesStep = isSplashback;
    this.isOnlySplashback = !isSplashback;

    const step = {
      index: 4,
      title: 'workbench.steps.finishes',
      injectionToken: WB_FINISHES,
      gtmTitle: 'Plan de travail | Finitions',
      gtmPageName: 'finitions',
    };

    this.updateStepperItems.emit({ isInsert: isSplashback, step });
    this.splashbackChanged.emit(this.isOnlySplashback);
  }

  onDisableNextButton(value: boolean) {
    this.disableNextButton.emit(value);
  }
}
